import Fuse from 'fuse.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSnapshot } from 'valtio'
import { Filter } from '@/components/Filter/Filter'
import { ItemList } from '@/components/ItemList/ItemList'
import PageWithRadarHeader from '@/components/Radar/PageWithRadarHeader'
import { Tags } from '@/components/Tags/Tags'
import { state, useSyncedSearchParams } from '@/lib/state'
import type { Item } from '../../../types'

const Overview = () => {
  const [searchParams, setSearchParams] = useSyncedSearchParams()
  const { radarConfig, radarItems } = useSnapshot(state)
  const labels = radarConfig?.labels || {}

  const [filterRing, setFilterRing] = useState<string | null>(null)
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<string | null>(null)

  const query = searchQuery || ''

  const title = labels.pageOverview

  useEffect(() => {
    const tag = searchParams.get('tag')
    setActiveTag(tag && tag !== 'null' ? tag : null)
    const ring = searchParams.get('ring')
    setFilterRing(ring && ring !== 'null' ? ring : null)
  }, [searchParams])

  // Memoize filteredItems
  const filteredItems = useMemo(() => {
    const tagToFilter = activeTag && activeTag !== 'null' ? activeTag : null
    return radarItems.filter(item => {
      const matchesTag = tagToFilter ? item.tags?.includes(tagToFilter) : true
      // Find latest release by date
      const latestRelease = item.releases.reduce((latest, current) => {
        if (!latest) return current
        return new Date(current.releaseDate) > new Date(latest.releaseDate)
          ? current
          : latest
      }, item.releases[0])
      const matchesRing = filterRing
        ? latestRelease && latestRelease.ring === filterRing
        : true
      return matchesTag && matchesRing
    })
  }, [radarItems, activeTag, filterRing])

  // Memoize Fuse index
  const fuseIndex = useMemo(() => {
    return new Fuse(filteredItems, {
      threshold: 0.5,
      distance: 600,
      includeScore: true,
      minMatchCharLength: 1,
      keys: [
        { name: 'releases.title', weight: 1.5 },
        { name: 'releases.body', weight: 1 },
        { name: 'tags', weight: 1 }
      ]
    })
  }, [filteredItems])

  const searchedItems = query
    ? fuseIndex.search(query).map(result => result.item)
    : filteredItems

  const onRingChange = useCallback(
    (ring: string) => {
      if (ring) {
        setFilterRing(ring)
        searchParams.set('ring', ring)
        searchParams.delete('tag')
        setSearchParams(searchParams)
      } else {
        setFilterRing(null)
        searchParams.delete('ring')
        setSearchParams(searchParams)
      }
    },
    [setSearchParams, searchParams]
  )

  // Update the URL with the new tag so refresh comes back to the same tag
  const onTagClick = useCallback(
    (tag: string | null) => {
      if (tag) {
        searchParams.set('tag', tag)
        setActiveTag(tag)
      } else {
        searchParams.delete('tag')
        setActiveTag(null)
      }
      setSearchParams(searchParams)
    },
    [setSearchParams, searchParams]
  )

  return (
    <PageWithRadarHeader title={title}>
      <Filter
        query={query}
        onQueryChange={q => {
          setSearchQuery(q)
        }}
        ring={filterRing || undefined}
        onRingChange={onRingChange}
        tags={radarItems.reduce<string[]>((acc, item) => {
          if (item.tags) acc.push(...item.tags)
          return acc
        }, [])}
        activeTag={activeTag}
        onTagClick={onTagClick}
      />

      {/* Tag filter bar below Filter (restored) */}
      {(() => {
        // Get all tags for display (deduplicated and sorted)
        const tagSet = new Set<string>()
        for (const item of radarItems) {
          if (item.tags) {
            for (const tag of item.tags) {
              tagSet.add(tag)
            }
          }
        }
        const tags = Array.from(tagSet).sort()
        return tags.length > 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '-16px',
              paddingBottom: '0',
              paddingTop: '10px'
            }}
          >
            <div style={{ maxWidth: 700, width: '100%' }}>
              <Tags
                tags={tags}
                activeTag={activeTag || undefined}
                onTagClick={onTagClick}
                hideLabel
              />
            </div>
          </div>
        ) : null
      })()}

      <ItemList items={searchedItems as Item[]} size="large" />
    </PageWithRadarHeader>
  )
}

export default Overview
